import React, { useMemo } from 'react';

import { Modal, ScrollView, Text, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import LinearGradient from 'react-native-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import closeButton from 'assets/images/svgs/ico_close.svg';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';
import {
    DETAIL_MORE_INFO_OVERLAY_CLOSE_ICON,
    DETAIL_MORE_INFO_OVERLAY_DESCRIPTION,
    DETAIL_MORE_INFO_OVERLAY_ID,
    DETAIL_MORE_INFO_OVERLAY_TITLE,
} from 'config/testID';

import { CastCrewPerson, Roles } from 'store/railsBuilder/reducer.types';

import { blackTransparent } from 'utils/colors';
import { HEADER_HEIGHT } from 'utils/hooks/useHeaderHeight';
import useLocalizedPersonName from 'utils/hooks/useLocalizedPersonName';
import { isAndroid15, isWeb } from 'utils/responsiveness';

import SvgButton from 'components/CustomButtons/SvgButton';
import { getFormattedCrewString } from 'components/Description/utils';

import styles from './styles';

type Props = {
    id: string;
    title?: string;
    description?: string | null;
    visible: boolean;
    castCrew?: CastCrewPerson[];
    onClose(): void;
};

const {
    ACCESSIBILITY: {
        LOGIN: { ACCESSIBILITY_CLOSE_BUTTON },
    },
} = LANGUAGE_KEYS;

const bottomGradientColors = [
    blackTransparent(0),
    blackTransparent(0.008),
    blackTransparent(0.03),
    blackTransparent(0.08),
    blackTransparent(0.14),
    blackTransparent(0.23),
    blackTransparent(0.33),
    blackTransparent(0.44),
    blackTransparent(0.55),
    blackTransparent(0.66),
    blackTransparent(0.76),
    blackTransparent(0.85),
    blackTransparent(0.91),
    blackTransparent(0.96),
    blackTransparent(0.99),
    '#000000',
];

function DescriptionOverlay({
    id = '',
    title = 'Title',
    description = '',
    visible,
    castCrew = [],
    onClose,
}: Props) {
    const { t } = useTranslation();
    const { top, bottom } = useSafeAreaInsets();
    const { getLocalizedPersonName } = useLocalizedPersonName();

    const castNames = useMemo(() => {
        const actors: string[] = [];
        const directors: string[] = [];

        if (!castCrew) return null;

        const cast = castCrew.filter((person) => person.type === 'cast');
        cast.forEach((person) => {
            const localizedName = getLocalizedPersonName(person.names);
            const selectedPlaylistIndex = person.playlist.findIndex(
                (playlist) => playlist.id === id,
            );

            if (selectedPlaylistIndex > -1) {
                if (
                    person.playlist[selectedPlaylistIndex].role.some(
                        (role) => role === Roles.DIRECTOR,
                    )
                ) {
                    directors.push(localizedName);
                } else {
                    actors.push(localizedName);
                }
            }
        });

        const formattedActors = getFormattedCrewString(actors);
        const formattedDirectors = getFormattedCrewString(directors);

        return { actors: formattedActors, directors: formattedDirectors };
    }, [castCrew, getLocalizedPersonName]);

    return (
        <Modal
            animationType="fade"
            transparent
            visible={visible}
            onRequestClose={onClose}
            testID={DETAIL_MORE_INFO_OVERLAY_ID}
            statusBarTranslucent={isAndroid15}
        >
            <View
                style={[
                    styles.safeArea,
                    { paddingTop: top, paddingBottom: bottom + HEADER_HEIGHT },
                ]}
            >
                <View
                    style={styles.header}
                    testID={DETAIL_MORE_INFO_OVERLAY_CLOSE_ICON}
                >
                    <SvgButton
                        style={styles.closeButton}
                        Svg={closeButton}
                        onPress={onClose}
                        accessibilityLabel={t(ACCESSIBILITY_CLOSE_BUTTON)}
                        tabIndex={0}
                    />
                </View>

                <View style={gStyles.flex}>
                    <ScrollView
                        bounces
                        scrollEventThrottle={16}
                        showsVerticalScrollIndicator={false}
                        contentContainerStyle={styles.scrollContainer}
                    >
                        <View style={styles.container}>
                            <Text
                                style={styles.titleText}
                                testID={DETAIL_MORE_INFO_OVERLAY_TITLE}
                                {...(isWeb && { tabIndex: 0 })}
                            >
                                {title}
                            </Text>
                            <Text
                                style={styles.descriptionText}
                                testID={DETAIL_MORE_INFO_OVERLAY_DESCRIPTION}
                                {...(isWeb && { tabIndex: 0 })}
                            >
                                {description}
                            </Text>
                            {!!castNames?.actors && (
                                <Text
                                    style={[styles.descriptionText]}
                                    {...(isWeb && { tabIndex: 0 })}
                                >{`${t('SCREEN_DETAIL_OVERLAY_CAST')}: ${
                                    castNames.actors
                                }`}</Text>
                            )}

                            {!!castNames?.directors && (
                                <Text
                                    style={[styles.descriptionText]}
                                    {...(isWeb && { tabIndex: 0 })}
                                >{`${t('SCREEN_DETAIL_OVERLAY_DIRECTOR')}: ${
                                    castNames.directors
                                }`}</Text>
                            )}
                        </View>
                    </ScrollView>

                    <LinearGradient
                        style={styles.topGradient}
                        colors={[...bottomGradientColors].reverse()}
                    />
                    <LinearGradient
                        style={styles.bottomGradient}
                        colors={bottomGradientColors}
                    />
                </View>
            </View>
        </Modal>
    );
}

export default DescriptionOverlay;
